export interface PlcAdapter {
	code?: string
	alias?: string
	children?: { [key: string]: PlcAdapter | string }
}

export type PlcValue = {
	code: string
	alias: string
}

export type PlcRef = { 
	context: PlcAdapter, 
	path: string,
	params?: Record<string, any>,
}



export class Adapter {
	/**
	 * Restituisce un oggetto PlcValue con i valori presi dall'albero di dati PlcAdapter
	 * @param adapter L'albero di configurazione
	 * @param path Il percorso separato da punti (es. "tanks.EXTRA")
	 * @param params Parametri opzionali per sostituire i placeholder nell'alias 
	 * per esempio se ho un ALIAS "COLUM_${colum}_ROW_${row}" e "params" { colum: "N1", row: "R3" }
	 * il risultato sarà "COLUM_N1_ROW_R3"
	 */
	static Get(adapter: PlcAdapter, path: string, params?: Record<string, any>): PlcValue | null {
		if ( adapter == null ) return null;
		const parts = (path ?? "").split('.');
		let currentAdapter = adapter;
		let currentCode: string = adapter.code ?? "";

		for (let i = 0; i < parts.length; i++) {
			const part = parts[i];
			if (!currentAdapter.children) return null;
			
			const node = currentAdapter.children[part];

			if (!node) return null;

			// Nodo è una stringa
			if (typeof node === 'string') {
				return {
					code: currentCode,
					alias: Adapter.format(node, params)
				}
			// È un oggetto
			} else {
				if (!!node.code) currentCode = node.code;

				if (i === parts.length - 1) {
					// Ultimo elemento del path
					if (node.alias) {
						return {
							code: currentCode,
							alias: Adapter.format(node.alias, params)
						};
					}
					return null;
				} else {
					// Continua la navigazione
					currentAdapter = node;
				}
			}
		}
		return null;
	}

	/**
	 * Restituisce un array di oggetti PlcValue per una lista di percorsi
	 * @param adapter L'albero di configurazione
	 * @param paths Array di percorsi separati da punti
	 * @param params Parametri opzionali per sostituire i placeholder nell'alias
	 */
	static GetList(adapter: PlcAdapter, paths: string[], params?: Record<string, string>): PlcValue[] {
		const result: PlcValue[] = [];
		for (const path of paths) {
			const value = Adapter.Get(adapter, path, params);
			if (value) result.push(value);
		}
		return result;
	}

	/**
	 * Restituisce un dizionario con key = path e value = PlcValue
	 * @param adapter L'albero di configurazione
	 * @param paths Array di percorsi separati da punti
	 * @param params Parametri opzionali per sostituire i placeholder nell'alias
	 */
	static GetDict(adapter: PlcAdapter, paths: string[], params?: Record<string, string>): Record<string, PlcValue> {
		const result: Record<string, PlcValue> = {};
		for (const path of paths) {
			const value = Adapter.Get(adapter, path, params);
			if (value) result[path] = value;
		}
		return result;
	}

	/**
	 * Aggiorna un nodo dell'albero di configurazione
	 * @param adapter L'albero di configurazione
	 * @param path Il percorso separato da punti (es. "tanks.EXTRA")
	 * @param changes Le modifiche da applicare al nodo
	 */
	static Update(adapter: PlcAdapter, path: string, changes: Record<string, any>): PlcAdapter {
		if (path === "") {
			Object.assign(adapter, changes);
			return adapter
		}

		const parts = path.split('.');
		let currentAdapter = adapter;

		for (let i = 0; i < parts.length; i++) {
			const part = parts[i];
			if (!currentAdapter.children) return adapter

			const node = currentAdapter.children[part];

			if (!node) return adapter

			if (i === parts.length - 1) {
				// Trovato il nodo da aggiornare
				if (typeof node === 'string') {
					currentAdapter.children[part] = {
						alias: node,
						...changes
					};
				} else {
					currentAdapter.children[part] = {
						...node,
						...changes
					};
				}
			} else {
				// Navigazione
				if (typeof node === 'string') {
					return adapter
				}
				currentAdapter = node;
			}
		}
	}

	private static format(alias: string, params?: Record<string, string>): string {
		if (!params) return alias;
		return alias.replace(/\$\{([a-zA-Z0-9_]+)\}/g, (match, key) => {
			return (params[key] !== undefined ? params[key] : match)?.toString() ?? "";
		});
	}
}